import type { ContentDatabase } from "@-/dev.improve.haniverse.database.content";
import type { StaticDatabase } from "@-/dev.improve.haniverse.database.static";
import type {
  ComponentButtonMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import runes from "@-/com.npmjs.runes2";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { hasLength, hasMinLength } from "@-/com.npmjs.ts-array-length";

function toMorphemeTier(
  tier: number | null | undefined,
): GraphemeMorphemeMnemonicsPanelMorphemeSlice["tier"] {
  if (tier === 1 || tier === 2 || tier === 3) {
    return tier;
  }
  return null;
}

function coerceMedianPolyline(
  median: [number, number][] | null | undefined,
): NonNullable<GraphemeMorphemeMnemonicsPanelMorphemeSlice["graphemeStrokes"]>[number]["median"] {
  if (median === null || median === undefined) {
    return [];
  }
  const out: NonNullable<
    GraphemeMorphemeMnemonicsPanelMorphemeSlice["graphemeStrokes"]
  >[number]["median"] = [];
  for (const tuple of median) {
    if (!Array.isArray(tuple) || !hasMinLength(tuple, 2)) {
      continue;
    }
    const x = Math.trunc(Number(tuple[0]));
    const y = Math.trunc(Number(tuple[1]));
    if (Number.isFinite(x) && Number.isFinite(y)) {
      out.push([x, y]);
    }
  }
  return out;
}

type GraphemeRow = {
  glyph: string;
  id: number;
  strokeCount: number | null;
  // Strokes live in the 1:1 `grapheme_stroke_data` relation, so the encoded blob is read
  // only by callers that render strokes (and never bloats id→glyph lookups). The relation row
  // is `null` when the grapheme has no Hanzi Writer data; the inner `strokeData` column is
  // itself nullable.
  strokeData: { strokeData: { medians: [number, number][]; path: string }[] | null } | null;
};

// A static grapheme together with the info data needed to render the panel:
// its definitions, PRC tier, and decomposition components (each carrying their
// own definitions and tier). All resolved in one relational query.
type GraphemeWithRefData = GraphemeRow & {
  components: {
    componentGrapheme: GraphemeRow & {
      definitions: DefinitionRow[];
      prcData: { tier: number | null } | null;
    };
  }[];
  definitions: DefinitionRow[];
  ids: readonly [string, ...number[]] | null;
  prcData: { tier: number | null } | null;
};

type MeaningCardRow = {
  back: string | null;
  description: string | null;
  final: boolean;
  grapheme: string;
  isDescriptionPremium: boolean;
  label: string | null;
  mnemonic: string | null;
};

/**
 * Pronunciation sibling card row. Each sibling shares its grapheme with the
 * meaning card and carries the pinyin in `pinyin`, the displayable phrase in
 * `phrase`, the image-generation prompt in `prompt`, and the disambiguating
 * gloss in `gloss`.
 */
type PronunciationCardRow = {
  gloss: string;
  grapheme: string;
  hsk2026Level: number | null;
  isMain: boolean;
  phrase: string | null;
  pinyin: string;
  prompt: string | null;
  tbclLevel: number | null;
  usage: { exhaustive: boolean; words: string[] } | null;
};

type DefinitionRow = {
  definition: string;
  pinyin: string;
};

const graphemeRefDataWith = {
  components: {
    columns: {},
    orderBy: { id: "asc" },
    with: {
      componentGrapheme: {
        columns: { glyph: true, id: true, strokeCount: true },
        with: {
          definitions: { columns: { definition: true, pinyin: true } },
          prcData: { columns: { tier: true } },
          strokeData: { columns: { strokeData: true } },
        },
      },
    },
  },
  definitions: { columns: { definition: true, pinyin: true } },
  prcData: { columns: { tier: true } },
  strokeData: { columns: { strokeData: true } },
} as const;

async function readMorphemeMnemonicsPanelGraphemeCard(
  contentDb: ContentDatabase,
  staticDb: StaticDatabase,
  front: string,
): Promise<GraphemeMorphemeMnemonicsPanelMorphemeSlice | null> {
  // The meaning card together with its pronunciation cards (joined on
  // `grapheme`) and mnemonic aliases, plus the static grapheme together with
  // its definitions, tier, and decomposition components.
  const [card, matchedGrapheme] = await Promise.all([
    contentDb(import.meta.location).query.appGraphemeMeaningCard.findFirst({
      columns: {
        final: true,
        gloss: true,
        grapheme: true,
        isDescriptionPremium: true,
        label: true,
      },
      where: { grapheme: front },
      with: {
        aliases: { columns: { alias: true }, orderBy: { position: "asc" } },
        description: { columns: { description: true, mnemonic: true } },
        pronunciationCards: {
          columns: {
            gloss: true,
            grapheme: true,
            hsk2026Level: true,
            isMain: true,
            phrase: true,
            pinyin: true,
            prompt: true,
            tbclLevel: true,
            usage: true,
          },
        },
      },
    }),
    staticDb(import.meta.location).query.grapheme.findFirst({
      columns: { glyph: true, id: true, ids: true, strokeCount: true },
      where: { glyph: front },
      with: graphemeRefDataWith,
    }),
  ]);

  if (card === undefined || matchedGrapheme === undefined) {
    return null;
  }

  const cardRow: MeaningCardRow = {
    back: card.gloss,
    description: card.description?.description ?? null,
    final: card.final,
    grapheme: card.grapheme,
    isDescriptionPremium: card.isDescriptionPremium,
    label: card.label,
    mnemonic: card.description?.mnemonic ?? null,
  };

  const definitionList = assembleDefinitionList(matchedGrapheme.definitions);
  const components = await assembleComponentButtonMorphemeSliceList(contentDb, matchedGrapheme);

  return buildMorphemeMnemonicsPanelCard({
    card: cardRow,
    components,
    definitionList,
    definitionRows: matchedGrapheme.definitions,
    graphemesForFront: [matchedGrapheme],
    matchedGrapheme,
    mnemonicTerms: card.aliases.map((r) => r.alias),
    pronunciationCardRows: card.pronunciationCards,
    tier: matchedGrapheme.prcData?.tier ?? null,
  });
}

async function readMorphemeMnemonicsPanelWordCard(
  contentDb: ContentDatabase,
  staticDb: StaticDatabase,
  front: string,
): Promise<GraphemeMorphemeMnemonicsPanelMorphemeSlice | null> {
  // For words the static dictionary entry (`word` + `word_definition`) drives
  // the meaning/pinyin lists, and each constituent character is rendered as a
  // component button with its own glyph, strokes, definitions, and tier.
  const characterGlyphs = runes(front);
  const matchedWord = await staticDb(import.meta.location).query.word.findFirst({
    columns: { id: true, word: true },
    where: { word: front },
    with: {
      definitions: {
        columns: { definition: true, pinyin: true },
        orderBy: { index: "asc" },
      },
    },
  });
  if (matchedWord === undefined) {
    return null;
  }

  const characterGraphemeRows = hasLength(characterGlyphs, 0)
    ? []
    : await staticDb(import.meta.location).query.grapheme.findMany({
        columns: { glyph: true, id: true, strokeCount: true },
        where: { glyph: { in: uniq(characterGlyphs) } },
        with: {
          definitions: { columns: { definition: true, pinyin: true } },
          prcData: { columns: { tier: true } },
          strokeData: { columns: { strokeData: true } },
        },
      });

  // Order character rows to match the word's own character sequence so the
  // rendered strokes and component buttons follow reading order. Each unique
  // glyph contributes once even when the word repeats it (e.g. "一一").
  type CharacterGraphemeRow = (typeof characterGraphemeRows)[number];
  const graphemeByGlyph = new Map<string, CharacterGraphemeRow>(
    characterGraphemeRows.map((row) => [row.glyph, row]),
  );
  const orderedCharacterGraphemeRows: CharacterGraphemeRow[] = [];
  const seenGlyphs = new Set<string>();
  for (const glyph of characterGlyphs) {
    if (seenGlyphs.has(glyph)) {
      continue;
    }
    const row = graphemeByGlyph.get(glyph);
    if (row === undefined) {
      continue;
    }
    seenGlyphs.add(glyph);
    orderedCharacterGraphemeRows.push(row);
  }

  const definitionList = assembleDefinitionList(matchedWord.definitions);
  const components = await assembleWordComponentButtonMorphemeSliceList(
    contentDb,
    orderedCharacterGraphemeRows,
  );
  const pinyinList = pinyinListFromDefinitions(matchedWord.definitions);

  return {
    back: undefined,
    components,
    definitionList,
    description: null,
    grapheme: matchedWord.word,
    graphemeId: matchedWord.id,
    graphemeStrokes: buildGraphemeStrokes(orderedCharacterGraphemeRows),
    isDescriptionPremium: false,
    mnemonic: null,
    mnemonicTerms: [],
    pinyinList: buildPinyinList({ pinyinList, pronunciationCardRows: [] }),
    strokeCount: orderedCharacterGraphemeRows[0]?.strokeCount,
    tier: null,
  };
}

async function assembleWordComponentButtonMorphemeSliceList(
  contentDb: ContentDatabase,
  characterGraphemeRows: readonly (GraphemeRow & {
    definitions: DefinitionRow[];
    prcData: { tier: number | null } | null;
  })[],
): Promise<ComponentButtonMorphemeSlice[]> {
  if (hasLength(characterGraphemeRows, 0)) {
    return [];
  }

  const componentFrontList = uniq(characterGraphemeRows.map((g) => g.glyph));
  const [componentPronunciationCardRows, componentLabelsByGlyph] = await Promise.all([
    contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
      columns: {
        gloss: true,
        grapheme: true,
        hsk2026Level: true,
        isMain: true,
        phrase: true,
        pinyin: true,
        prompt: true,
        tbclLevel: true,
        usage: true,
      },
      where: { grapheme: { in: componentFrontList } },
    }) as Promise<PronunciationCardRow[]>,
    readComponentLabelsByGlyph(contentDb, componentFrontList),
  ]);

  return characterGraphemeRows.map((grapheme) => {
    const componentPronunciationRows = componentPronunciationCardRows.filter(
      (p) => p.grapheme === grapheme.glyph,
    );
    const graphemeStrokes = buildGraphemeStrokes([grapheme]);
    return {
      back: componentLabelsByGlyph.get(grapheme.glyph)?.back,
      componentMnemonic: componentLabelsByGlyph.get(grapheme.glyph)?.componentMnemonic,
      componentMnemonicTerms: componentLabelsByGlyph.get(grapheme.glyph)?.componentMnemonicTerms,
      graphemes: [{ glyph: grapheme.glyph, strokes: graphemeStrokes }],
      graphemeStrokes,
      isMeaningMnemonic: componentLabelsByGlyph.get(grapheme.glyph)?.isMeaningMnemonic,
      pinyin: buildComponentButtonPinyin({
        pinyinList: pronunciationPinyinList(grapheme.definitions, componentPronunciationRows),
        pronunciationCardRows: componentPronunciationRows,
      }),
      tier: toMorphemeTier(grapheme.prcData?.tier),
    };
  });
}

function assembleDefinitionList(
  definitionRows: DefinitionRow[],
): GraphemeMorphemeMnemonicsPanelMorphemeSlice["definitionList"] {
  const entriesByPinyin = new Map<string, Set<string>>();
  const pinyinOrder: string[] = [];
  for (const row of definitionRows) {
    let entries = entriesByPinyin.get(row.pinyin);
    if (entries === undefined) {
      entries = new Set();
      entriesByPinyin.set(row.pinyin, entries);
      pinyinOrder.push(row.pinyin);
    }
    entries.add(row.definition);
  }
  return pinyinOrder.map((pinyin) => ({
    entries: [...(entriesByPinyin.get(pinyin) ?? [])],
    pinyin,
  }));
}

/**
 * A character's pronunciations: its dictionary readings that have a pronunciation card, in
 * dictionary order. A reading the card leaves blank (`wéi:` with no gloss) and every reading of a
 * primitive card (tier `0` / `obscure`) has none, so it is not shown as a pronunciation — the
 * dictionary definitions still list it.
 */
function pronunciationPinyinList(
  definitionRows: DefinitionRow[],
  pronunciationCardRows: readonly PronunciationCardRow[],
): string[] {
  const cardPinyinSet = new Set(pronunciationCardRows.map(({ pinyin }) => pinyin));
  return pinyinListFromDefinitions(definitionRows).filter((pinyin) => cardPinyinSet.has(pinyin));
}

function pinyinListFromDefinitions(definitionRows: DefinitionRow[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of definitionRows) {
    if (!seen.has(row.pinyin)) {
      seen.add(row.pinyin);
      out.push(row.pinyin);
    }
  }
  return out;
}

async function assembleComponentButtonMorphemeSliceList(
  contentDb: ContentDatabase,
  matchedGrapheme: GraphemeWithRefData,
): Promise<ComponentButtonMorphemeSlice[]> {
  const orderedComponentGraphemeList = matchedGrapheme.components.map(
    ({ componentGrapheme }) => componentGrapheme,
  );

  if (hasLength(orderedComponentGraphemeList, 0)) {
    return [];
  }

  // Fetch shared component metadata once per glyph, but keep every ordered decomposition
  // occurrence in the returned slice (for example, 二 is 一 + 一).
  const componentFrontList = uniq(orderedComponentGraphemeList.map((g) => g.glyph));

  // Pronunciation cards (for the reading gloss/audio) and meaning cards (for the
  // component's mnemonic label) both live in the content DB, so they're fetched
  // separately from the static decomposition data.
  const [componentPronunciationCardRows, componentLabelsByGlyph] = await Promise.all([
    contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
      columns: {
        gloss: true,
        grapheme: true,
        hsk2026Level: true,
        isMain: true,
        phrase: true,
        pinyin: true,
        prompt: true,
        tbclLevel: true,
        usage: true,
      },
      where: { grapheme: { in: componentFrontList } },
    }) as Promise<PronunciationCardRow[]>,
    readComponentLabelsByGlyph(contentDb, componentFrontList),
  ]);

  const out: ComponentButtonMorphemeSlice[] = [];
  for (const grapheme of orderedComponentGraphemeList) {
    const componentPronunciationRows = componentPronunciationCardRows.filter(
      (p) => p.grapheme === grapheme.glyph,
    );
    const graphemeStrokes = buildGraphemeStrokes([grapheme]);
    out.push({
      back: componentLabelsByGlyph.get(grapheme.glyph)?.back,
      componentMnemonic: componentLabelsByGlyph.get(grapheme.glyph)?.componentMnemonic,
      componentMnemonicTerms: componentLabelsByGlyph.get(grapheme.glyph)?.componentMnemonicTerms,
      graphemes: [{ glyph: grapheme.glyph, strokes: graphemeStrokes }],
      graphemeStrokes,
      isMeaningMnemonic: componentLabelsByGlyph.get(grapheme.glyph)?.isMeaningMnemonic,
      pinyin: buildComponentButtonPinyin({
        pinyinList: pronunciationPinyinList(grapheme.definitions, componentPronunciationRows),
        pronunciationCardRows: componentPronunciationRows,
      }),
      tier: toMorphemeTier(grapheme.prcData?.tier),
    });
  }

  return out;
}

type ComponentLabels = Pick<
  ComponentButtonMorphemeSlice,
  "back" | "componentMnemonic" | "componentMnemonicTerms" | "isMeaningMnemonic"
>;

/**
 * Loads component.mnemonic aliases and the fallback meaning-card gloss, keyed by glyph.
 */
async function readComponentLabelsByGlyph(
  contentDb: ContentDatabase,
  componentFrontList: readonly string[],
): Promise<Map<string, ComponentLabels>> {
  if (hasLength(componentFrontList, 0)) {
    return new Map();
  }
  const meaningCardRows = await contentDb(
    import.meta.location,
  ).query.appGraphemeMeaningCard.findMany({
    columns: { gloss: true, grapheme: true, label: true },
    where: { grapheme: { in: [...componentFrontList] } },
    with: { aliases: { columns: { alias: true }, orderBy: { position: "asc" } } },
  });
  const labelsByGlyph = new Map<string, ComponentLabels>();
  for (const row of meaningCardRows) {
    labelsByGlyph.set(row.grapheme, {
      back: row.gloss ?? undefined,
      componentMnemonic: row.aliases.find(({ alias }) => alias.trim() !== "")?.alias,
      componentMnemonicTerms: row.aliases.map(({ alias }) => alias),
      isMeaningMnemonic: row.label?.trim() === "mnemonic",
    });
  }
  return labelsByGlyph;
}

function buildComponentButtonPinyin({
  pinyinList,
  pronunciationCardRows,
}: {
  pinyinList: string[];
  pronunciationCardRows: PronunciationCardRow[];
}): ComponentButtonMorphemeSlice["pinyin"] {
  const pinyin = pinyinList[0];
  if (pinyin === undefined) {
    return { pinyin: "" };
  }
  const gloss = pronunciationCardRows.find((p) => p.pinyin === pinyin)?.gloss;
  return gloss === undefined ? { pinyin } : { gloss, pinyin };
}

function buildPinyinList({
  pinyinList,
  pronunciationCardRows,
}: {
  pinyinList: string[];
  pronunciationCardRows: PronunciationCardRow[];
}): GraphemeMorphemeMnemonicsPanelMorphemeSlice["pinyinList"] {
  const dataByPinyin = new Map<string, PronunciationCardRow>();
  for (const p of pronunciationCardRows) {
    if (dataByPinyin.has(p.pinyin)) {
      continue;
    }
    dataByPinyin.set(p.pinyin, p);
  }

  const out: GraphemeMorphemeMnemonicsPanelMorphemeSlice["pinyinList"] = [];
  for (const pinyin of pinyinList) {
    const matched = dataByPinyin.get(pinyin);
    out.push({
      gloss: matched?.gloss ?? null,
      hsk2026Level: matched?.hsk2026Level ?? null,
      isMain: matched?.isMain ?? false,
      mnemonic: matched?.phrase ?? null,
      tbclLevel: matched?.tbclLevel ?? null,
      toneNumbersPinyin: pinyin,
      usage: matched?.usage ?? null,
    });
  }
  return out;
}

/** `null` when any glyph has no stroke data (see `graphemeStrokeListSchema`). */
function buildGraphemeStrokes(
  graphemeRows: GraphemeRow[],
): GraphemeMorphemeMnemonicsPanelMorphemeSlice["graphemeStrokes"] {
  const graphemeStrokes: NonNullable<
    GraphemeMorphemeMnemonicsPanelMorphemeSlice["graphemeStrokes"]
  > = [];
  let strokeIndex = 0;
  for (const g of graphemeRows) {
    const strokeData = g.strokeData?.strokeData ?? null;
    if (strokeData === null) {
      return null;
    }
    for (const { medians, path } of strokeData) {
      graphemeStrokes.push({ index: strokeIndex, median: coerceMedianPolyline(medians), path });
      strokeIndex += 1;
    }
  }
  return graphemeStrokes;
}

function buildMorphemeMnemonicsPanelCard({
  card,
  components,
  definitionList,
  definitionRows,
  graphemesForFront,
  matchedGrapheme,
  mnemonicTerms,
  pronunciationCardRows,
  tier,
}: {
  card: MeaningCardRow;
  components: ComponentButtonMorphemeSlice[];
  definitionList: GraphemeMorphemeMnemonicsPanelMorphemeSlice["definitionList"];
  definitionRows: DefinitionRow[];
  graphemesForFront: GraphemeRow[];
  matchedGrapheme: GraphemeRow;
  mnemonicTerms: string[];
  pronunciationCardRows: PronunciationCardRow[];
  tier: number | null;
}): GraphemeMorphemeMnemonicsPanelMorphemeSlice {
  const pinyinList = pronunciationPinyinList(definitionRows, pronunciationCardRows);
  return {
    back: card.back ?? undefined,
    components,
    definitionList,
    description: card.description || null,
    final: card.final,
    grapheme: matchedGrapheme.glyph,
    graphemeId: matchedGrapheme.id,
    graphemeStrokes: buildGraphemeStrokes(graphemesForFront),
    isDescriptionPremium: card.isDescriptionPremium,
    label: card.label,
    mnemonic: card.mnemonic || null,
    // Hermes does not support Array.prototype.toSorted; keep the query data immutable.
    mnemonicTerms: [...mnemonicTerms].sort(
      (left, right) => Number(right === card.back) - Number(left === card.back),
    ),
    pinyinList: buildPinyinList({ pinyinList, pronunciationCardRows }),
    strokeCount: matchedGrapheme.strokeCount,
    tier: toMorphemeTier(tier),
  };
}

export function useGetMorphemeMnemonicsPanelMorphemeSliceQueryKey() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);

  return (grapheme: string) =>
    ["morphemeMnemonicsPanelCard", grapheme, { contentDb, staticDb }] as const;
}

export const [
  useMorphemeMnemonicsPanelMorphemeSliceQueryOptions,
  morphemeMnemonicsPanelMorphemeSliceQueryFn,
] = createUseQueryOptions(
  useGetMorphemeMnemonicsPanelMorphemeSliceQueryKey,
  ({ queryKey: [_, grapheme, { contentDb, staticDb }] }) => {
    // Words (multi-codepoint fronts like "一个") and single graphemes resolve
    // through different tables, so dispatch by codepoint count rather than try
    // the grapheme path first and fall back.
    if (!hasMinLength(runes(grapheme), 2)) {
      return readMorphemeMnemonicsPanelGraphemeCard(contentDb, staticDb, grapheme);
    }
    return readMorphemeMnemonicsPanelWordCard(contentDb, staticDb, grapheme);
  },
);

// Reads only the content + static DBs (both bundled / read-only on-device) and the key carries no
// change-version, so the resolved card never goes stale at runtime; the global QueryClient default
// `staleTime: Infinity` caches it so a remount resolves from cache. The MUTABLE user-facing state
// (favorites, study progress, mnemonic overrides) lives in the SEPARATE user-slice query, which
// stays reactive via its change-version.

export function useGetMorphemeMnemonicsPanelCardsByGraphemeIdsSliceQueryKey() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);

  return (graphemeIds: readonly number[]) => {
    const sortedKey = [...graphemeIds].sort((a, b) => a - b);
    return [
      "morphemeMnemonicsPanelCardsByGraphemeIdsSlice",
      sortedKey,
      { contentDb, staticDb },
    ] as const;
  };
}

export const [
  useMorphemeMnemonicsPanelCardsByGraphemeIdsSliceQueryOptions,
  morphemeMnemonicsPanelCardsByGraphemeIdsSliceQueryFn,
] = createUseQueryOptions(
  useGetMorphemeMnemonicsPanelCardsByGraphemeIdsSliceQueryKey,
  async ({ queryKey: [_, graphemeIds, { contentDb, staticDb }] }) => {
    if (hasLength(graphemeIds, 0)) {
      return [];
    }
    const uniqueIds = uniq([...graphemeIds]);
    const graphemeRows = await staticDb(import.meta.location).query.grapheme.findMany({
      columns: { glyph: true, id: true },
      where: { id: { in: uniqueIds } },
    });
    if (hasLength(graphemeRows, 0)) {
      return [];
    }

    const glyphById = new Map(graphemeRows.map((g) => [g.id, g.glyph]));
    // Read every card CONCURRENTLY. Each `read…Card` issues its own static/content queries, which
    // in `online` mode (the default) are HTTP round-trips to the worker — awaiting them one at a
    // time made a panel of N graphemes cost N serialized round-trips (invisible against local
    // SQLite, seconds against Cloudflare). `Promise.all` preserves input order, so the resulting
    // slice order still matches `graphemeIds`; the `null`s are filtered after the fact rather than
    // inside the loop.
    const cards = await Promise.all(
      graphemeIds.map(async (id) => {
        const glyph = glyphById.get(id);
        if (glyph === undefined) {
          return null;
        }
        return !hasMinLength(runes(glyph), 2)
          ? readMorphemeMnemonicsPanelGraphemeCard(contentDb, staticDb, glyph)
          : readMorphemeMnemonicsPanelWordCard(contentDb, staticDb, glyph);
      }),
    );
    return cards.filter(
      (card): card is GraphemeMorphemeMnemonicsPanelMorphemeSlice => card !== null,
    );
  },
);
